import React, { useEffect, useState, useCallback } from "react";
import {
  logsApi,
  ServiceInfo,
  ServiceDetail,
  Comment as CommentType,
  LogEntry,
} from "../services/logsApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Server,
  AlertTriangle,
  Activity,
  Clock,
  ChevronRight,
  ArrowLeft,
  MessageSquare,
  Send,
  Trash2,
  RefreshCw,
  Zap,
  CheckCircle2,
  AlertCircle,
  User,
} from "lucide-react";
import Skeleton, {
  SkeletonCard,
  SkeletonChart,
  SkeletonTable,
} from "./ui/Skeleton";

const t = {
  primary: "#7c3aed",
  primaryMid: "#a78bfa",
  primaryLight: "#ede9fe",
  surface: "#ffffff",
  border: "#e9e5f5",
  danger: "#ef4444",
  dangerBg: "#fee2e2",
  warn: "#f59e0b",
  warnBg: "#fef3c7",
  success: "#22c55e",
  successBg: "#dcfce7",
  text: {
    primary: "#1e1b4b",
    secondary: "#6b7280",
    muted: "#9ca3b8",
    inverse: "#ffffff",
  },
};

const LEVEL_COLORS: Record<string, { bg: string; color: string }> = {
  error: { bg: t.dangerBg, color: t.danger },
  warn: { bg: t.warnBg, color: t.warn },
  info: { bg: t.primaryLight, color: t.primary },
  debug: { bg: "#f1f5f9", color: "#475569" },
};

// ── Skeleton: Services List ──
function ServicesListSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton width={160} height={26} className="mb-2" />
          <Skeleton width={200} height={14} />
        </div>
        <Skeleton width={110} height={36} rounded="xl" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-2xl p-5"
            style={{
              backgroundColor: t.surface,
              border: `1px solid ${t.border}`,
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Skeleton width={40} height={40} rounded="xl" />
              <div className="flex-1">
                <Skeleton width={100} height={16} className="mb-1" />
                <Skeleton width={140} height={12} />
              </div>
              <Skeleton width={20} height={20} rounded="full" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Skeleton width="100%" height={12} className="mb-1" />
                <Skeleton width="60%" height={18} />
              </div>
              <div>
                <Skeleton width="100%" height={12} className="mb-1" />
                <Skeleton width="60%" height={18} />
              </div>
              <div>
                <Skeleton width="100%" height={12} className="mb-1" />
                <Skeleton width="60%" height={18} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton: Service Detail ──
function ServiceDetailSkeleton() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Skeleton width={36} height={36} rounded="xl" />
        <div>
          <Skeleton width={180} height={24} className="mb-1" />
          <Skeleton width={120} height={14} />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
      <div className="grid grid-cols-12 gap-5 mb-5">
        <div className="col-span-12 lg:col-span-7">
          <SkeletonChart />
        </div>
        <div className="col-span-12 lg:col-span-5">
          <SkeletonTable rows={5} />
        </div>
      </div>
      <div
        className="rounded-2xl p-5"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        <Skeleton width={140} height={18} className="mb-4" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3 mb-4">
            <Skeleton width={36} height={36} rounded="full" />
            <div className="flex-1">
              <Skeleton width={100} height={14} className="mb-1" />
              <Skeleton width="80%" height={14} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Service Card ──
function ServiceCard({
  service,
  onClick,
}: {
  service: ServiceInfo;
  onClick: () => void;
}) {
  const errorRate = parseFloat(service.errorRate);
  const statusColor =
    errorRate > 10 ? t.danger : errorRate > 5 ? t.warn : t.success;
  const statusBg =
    errorRate > 10 ? t.dangerBg : errorRate > 5 ? t.warnBg : t.successBg;
  const statusLabel =
    errorRate > 10 ? "Critique" : errorRate > 5 ? "Attention" : "Sain";

  return (
    <button
      onClick={onClick}
      className="rounded-2xl p-5 text-left w-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group"
      style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
          style={{ backgroundColor: t.primaryLight }}
        >
          <Server className="w-5 h-5" style={{ color: t.primary }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[14px] font-bold truncate"
            style={{ color: t.text.primary }}
          >
            {service.name}
          </p>
          <p className="text-[11px]" style={{ color: t.text.muted }}>
            {service.lastActivity
              ? `Actif ${new Date(service.lastActivity).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
              : "Aucune activité"}
          </p>
        </div>
        <div className="flex items-center">
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full mr-2"
            style={{ backgroundColor: statusBg, color: statusColor }}
          >
            {statusLabel}
          </span>
          <ChevronRight
            className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
            style={{ color: t.text.muted }}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <p
            className="text-[10px] uppercase tracking-wide mb-0.5"
            style={{ color: t.text.muted }}
          >
            Appels
          </p>
          <p
            className="text-[16px] font-bold"
            style={{ color: t.text.primary }}
          >
            {service.total.toLocaleString()}
          </p>
        </div>
        <div>
          <p
            className="text-[10px] uppercase tracking-wide mb-0.5"
            style={{ color: t.text.muted }}
          >
            Erreurs
          </p>
          <p className="text-[16px] font-bold" style={{ color: t.danger }}>
            {service.errorCount.toLocaleString()}
          </p>
        </div>
        <div>
          <p
            className="text-[10px] uppercase tracking-wide mb-0.5"
            style={{ color: t.text.muted }}
          >
            Taux err.
          </p>
          <p className="text-[16px] font-bold" style={{ color: statusColor }}>
            {service.errorRate}%
          </p>
        </div>
      </div>

      {/* Level mini-bars */}
      <div
        className="flex gap-1 mt-3 h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: "#f3f0ff" }}
      >
        {Object.entries(service.levels).map(([level, count]) => (
          <div
            key={level}
            style={{
              width: `${(count / (service.total || 1)) * 100}%`,
              backgroundColor: LEVEL_COLORS[level]?.color || t.text.muted,
              minWidth: count > 0 ? "4px" : "0",
            }}
          />
        ))}
      </div>
    </button>
  );
}

// ── Comment Item ──
function CommentItem({
  comment,
  onDelete,
}: {
  comment: CommentType;
  onDelete: () => void;
}) {
  return (
    <div
      className="flex gap-3 py-3 transition-colors group"
      style={{ borderBottom: `1px solid ${t.border}` }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: t.primaryLight }}
      >
        <User className="w-3.5 h-3.5" style={{ color: t.primary }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="text-[13px] font-semibold"
              style={{ color: t.text.primary }}
            >
              {comment.author}
            </span>
            <span className="text-[11px]" style={{ color: t.text.muted }}>
              {new Date(comment.timestamp).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-red-50 transition-all"
            style={{ border: "none", background: "transparent" }}
          >
            <Trash2 className="w-3.5 h-3.5" style={{ color: t.danger }} />
          </button>
        </div>
        <p
          className="text-[12px] mt-0.5 leading-relaxed"
          style={{ color: t.text.secondary }}
        >
          {comment.content}
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// ── Main Component ──
// ══════════════════════════════════════════
export default function Services() {
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Detail view
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [detail, setDetail] = useState<ServiceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Comments
  const [comments, setComments] = useState<CommentType[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentAuthor, setCommentAuthor] = useState("Admin");
  const [submitting, setSubmitting] = useState(false);

  // Load services list
  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await logsApi.getServicesList();
      if (res.success)
        setServices(res.data.filter((s) => s.name !== "logs-api"));
    } catch (err) {
      console.error("Failed to load services:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // Load service detail + comments
  const openService = async (name: string) => {
    setSelectedService(name);
    setDetailLoading(true);
    setCommentsLoading(true);
    try {
      const [detailRes, commentsRes] = await Promise.all([
        logsApi.getServiceDetail(name),
        logsApi.getComments(name),
      ]);
      if (detailRes.success) setDetail(detailRes.data);
      if (commentsRes.success) setComments(commentsRes.data);
    } catch (err) {
      console.error("Failed to load service detail:", err);
    } finally {
      setDetailLoading(false);
      setCommentsLoading(false);
    }
  };

  const goBack = () => {
    setSelectedService(null);
    setDetail(null);
    setComments([]);
  };

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedService) return;
    setSubmitting(true);
    try {
      const res = await logsApi.addComment(
        selectedService,
        commentAuthor,
        newComment.trim(),
      );
      if (res.success) {
        setComments((prev) => [res.data, ...prev]);
        setNewComment("");
      }
    } catch (err) {
      console.error("Failed to add comment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId: string) => {
    if (!selectedService) return;
    try {
      await logsApi.deleteComment(selectedService, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  // ── Services List View ──
  if (!selectedService) {
    if (loading) return <ServicesListSkeleton />;

    return (
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2
              className="text-[22px] font-bold"
              style={{ color: t.text.primary }}
            >
              Services
            </h2>
            <p
              className="text-[13px] mt-0.5"
              style={{ color: t.text.secondary }}
            >
              {services.length} service{services.length > 1 ? "s" : ""} actif
              {services.length > 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={loadServices}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium text-white transition-all duration-200"
            style={{ backgroundColor: t.primary, border: "none" }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {services.map((svc) => (
            <ServiceCard
              key={svc.name}
              service={svc}
              onClick={() => openService(svc.name)}
            />
          ))}
          {services.length === 0 && (
            <div
              className="col-span-full text-center py-12 text-[13px]"
              style={{ color: t.text.muted }}
            >
              Aucun service trouvé
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Service Detail View ──
  if (detailLoading) return <ServiceDetailSkeleton />;

  const errorRate = parseFloat(detail?.errorRate || "0");
  const hourlyData =
    detail?.byHour?.slice(-24).map((h) => ({
      hour: h.key_as_string?.split(" ")[1] || h.key_as_string,
      count: h.doc_count,
    })) || [];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-gray-100"
            style={{ border: `1px solid ${t.border}`, background: t.surface }}
          >
            <ArrowLeft
              className="w-4 h-4"
              style={{ color: t.text.secondary }}
            />
          </button>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: t.primaryLight }}
          >
            <Server className="w-5 h-5" style={{ color: t.primary }} />
          </div>
          <div>
            <h2
              className="text-[22px] font-bold"
              style={{ color: t.text.primary }}
            >
              {detail?.name || selectedService}
            </h2>
            <p className="text-[13px]" style={{ color: t.text.secondary }}>
              Détails du service
            </p>
          </div>
        </div>
        <button
          onClick={() => openService(selectedService)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium text-white transition-all duration-200"
          style={{ backgroundColor: t.primary, border: "none" }}
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger-children">
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4" style={{ color: t.primary }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Total
            </span>
          </div>
          <p
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            {detail?.total?.toLocaleString() || "0"}
          </p>
        </div>
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" style={{ color: t.danger }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Erreurs
            </span>
          </div>
          <p className="text-[22px] font-bold" style={{ color: t.danger }}>
            {detail?.errorCount?.toLocaleString() || "0"}
          </p>
          <span className="text-[11px]" style={{ color: t.text.muted }}>
            {detail?.errorRate}% du total
          </span>
        </div>
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" style={{ color: t.warn }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Temps moy.
            </span>
          </div>
          <p
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            {detail?.avgResponseTime != null
              ? `${detail.avgResponseTime}ms`
              : "—"}
          </p>
        </div>
        <div
          className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4" style={{ color: t.success }} />
            <span
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: t.text.muted }}
            >
              Santé
            </span>
          </div>
          <p
            className="text-[22px] font-bold"
            style={{
              color:
                errorRate > 10 ? t.danger : errorRate > 5 ? t.warn : t.success,
            }}
          >
            {errorRate > 10 ? "Critique" : errorRate > 5 ? "Attention" : "Sain"}
          </p>
        </div>
      </div>

      {/* Row: Hourly Chart + Recent Logs */}
      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* Hourly Chart */}
        <div
          className="col-span-12 lg:col-span-7 rounded-2xl p-5 animate-fade-in-up"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-[15px] font-bold"
              style={{ color: t.text.primary }}
            >
              Activité Horaire
            </h3>
            <span
              className="text-[11px] font-medium px-2.5 py-1 rounded-full"
              style={{ backgroundColor: t.primaryLight, color: t.primary }}
            >
              {hourlyData.length} créneaux
            </span>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f0ff"
                  vertical={false}
                />
                <XAxis
                  dataKey="hour"
                  fontSize={10}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  fontSize={10}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                />
                <Bar
                  dataKey="count"
                  name="Appels"
                  fill={t.primary}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={18}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Logs */}
        <div
          className="col-span-12 lg:col-span-5 rounded-2xl p-5 animate-fade-in-up"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
            animationDelay: "80ms",
          }}
        >
          <h3
            className="text-[15px] font-bold mb-3"
            style={{ color: t.text.primary }}
          >
            Logs Récents
          </h3>
          <div className="space-y-0 max-h-[240px] overflow-y-auto">
            {detail?.recentLogs?.map((log, i) => {
              const ls = LEVEL_COLORS[log.level] || LEVEL_COLORS.info;
              return (
                <div
                  key={log.id || i}
                  className="flex items-start gap-2 py-2 transition-colors hover:bg-[#faf8ff]"
                  style={{ borderBottom: `1px solid ${t.border}` }}
                >
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 flex-shrink-0"
                    style={{ backgroundColor: ls.bg, color: ls.color }}
                  >
                    {log.level}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-[12px] truncate"
                      style={{ color: t.text.primary }}
                    >
                      {log.message}
                    </p>
                    <p className="text-[10px]" style={{ color: t.text.muted }}>
                      {log.path || "—"} ·{" "}
                      {new Date(log.timestamp).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
            {(!detail?.recentLogs || detail.recentLogs.length === 0) && (
              <p
                className="py-6 text-center text-[12px]"
                style={{ color: t.text.muted }}
              >
                Aucun log récent
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Level Breakdown */}
      {detail?.levels && Object.keys(detail.levels).length > 0 && (
        <div
          className="rounded-2xl p-5 mb-5 animate-fade-in-up"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
            animationDelay: "120ms",
          }}
        >
          <h3
            className="text-[15px] font-bold mb-3"
            style={{ color: t.text.primary }}
          >
            Répartition par Niveau
          </h3>
          <div className="flex gap-4 flex-wrap">
            {Object.entries(detail.levels).map(([level, count]) => {
              const ls = LEVEL_COLORS[level] || LEVEL_COLORS.info;
              const pct =
                detail.total > 0
                  ? ((count / detail.total) * 100).toFixed(1)
                  : "0";
              return (
                <div
                  key={level}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ backgroundColor: ls.bg }}
                >
                  <span
                    className="text-[12px] font-bold uppercase"
                    style={{ color: ls.color }}
                  >
                    {level}
                  </span>
                  <span
                    className="text-[16px] font-bold"
                    style={{ color: ls.color }}
                  >
                    {count.toLocaleString()}
                  </span>
                  <span
                    className="text-[11px] font-medium"
                    style={{ color: ls.color, opacity: 0.7 }}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div
        className="rounded-2xl p-5 animate-fade-in-up"
        style={{
          backgroundColor: t.surface,
          border: `1px solid ${t.border}`,
          animationDelay: "160ms",
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-4 h-4" style={{ color: t.primary }} />
          <h3
            className="text-[15px] font-bold"
            style={{ color: t.text.primary }}
          >
            Commentaires
          </h3>
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: t.primaryLight, color: t.primary }}
          >
            {comments.length}
          </span>
        </div>

        {/* Add comment form */}
        <div className="flex gap-3 mb-4">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: t.primary }}
          >
            <User className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={commentAuthor}
              onChange={(e) => setCommentAuthor(e.target.value)}
              placeholder="Nom..."
              className="w-24 px-3 py-2 rounded-lg text-[12px]"
              style={{
                border: `1px solid ${t.border}`,
                color: t.text.primary,
                backgroundColor: "#faf9fc",
                outline: "none",
              }}
            />
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
              placeholder="Ajouter un commentaire..."
              className="flex-1 px-3 py-2 rounded-lg text-[12px]"
              style={{
                border: `1px solid ${t.border}`,
                color: t.text.primary,
                backgroundColor: "#faf9fc",
                outline: "none",
              }}
            />
            <button
              onClick={handleAddComment}
              disabled={submitting || !newComment.trim()}
              className="px-4 py-2 rounded-lg text-white text-[12px] font-medium flex items-center gap-1.5 transition-all duration-200 disabled:opacity-40"
              style={{ backgroundColor: t.primary, border: "none" }}
            >
              <Send className="w-3.5 h-3.5" />
              Envoyer
            </button>
          </div>
        </div>

        {/* Comments list */}
        {commentsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex gap-3 py-3"
                style={{ borderBottom: `1px solid ${t.border}` }}
              >
                <Skeleton width={32} height={32} rounded="full" />
                <div className="flex-1">
                  <Skeleton width={120} height={14} className="mb-1" />
                  <Skeleton width="90%" height={14} />
                </div>
              </div>
            ))}
          </div>
        ) : comments.length > 0 ? (
          <div>
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onDelete={() => handleDeleteComment(comment.id)}
              />
            ))}
          </div>
        ) : (
          <p
            className="py-6 text-center text-[12px]"
            style={{ color: t.text.muted }}
          >
            Aucun commentaire pour ce service. Soyez le premier à commenter !
          </p>
        )}
      </div>
    </div>
  );
}
